/**
 * Workbook file I/O — SheetJS in the browser. Parsing never leaves the browser; export coercion
 * turns plain numbers back into numbers (never dates, never leading-zero ids such as phone numbers).
 */
import * as XLSX from 'xlsx';
import { autofitAll, DEFAULT_COL_W, MAX_PARSE_CELLS, nextId, type DocKind, type SheetData, type SheetDoc } from './sheet-model';

export interface ParseResult {
  doc: SheetDoc;
  /** Sheets that hit the cell cap and were cut off (name → kept rows). */
  truncated: { sheet: string; keptRows: number }[];
}

const XLSX_EXTS = /\.(xlsx|xlsm|xls|xlsb|ods)$/i;
const TEXT_EXTS = /\.(csv|tsv|txt)$/i;

export function isSpreadsheetName(name: string): boolean {
  return XLSX_EXTS.test(name) || TEXT_EXTS.test(name);
}

export const ACCEPT_SPREADSHEETS = '.csv,.tsv,.txt,.xlsx,.xlsm,.xls,.xlsb,.ods,text/csv,text/plain,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Excel width chars ↔ our pixel widths (Calibri ≈ 7px/char + padding). */
const wchToPx = (wch: number) => Math.round(wch * 7 + 5);
const pxToWch = (px: number) => Math.max(1, Math.round((px - 5) / 7));

// ── open ────────────────────────────────────────────────────────────────────

export function parseSpreadsheet(filename: string, data: ArrayBuffer, sourceLabel = 'Local file'): ParseResult {
  const isText = !XLSX_EXTS.test(filename);
  // CSV: let SheetJS sniff the delimiter; raw:false → formatted cell text (dates/currency as Excel shows them).
  const wb = isText ? XLSX.read(new Uint8Array(data), { type: 'array', raw: true, cellText: true, codepage: 65001 }) : XLSX.read(data, { type: 'array', cellStyles: true, dense: false });
  const truncated: ParseResult['truncated'] = [];

  const sheets: SheetData[] = wb.SheetNames.map((sheetName) => {
    const ws = wb.Sheets[sheetName];
    let rows: string[][] = ws
      ? (XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, raw: false, defval: '', blankrows: false }) as unknown[][]).map((row) => row.map((v) => (v == null ? '' : String(v))))
      : [];
    // Drop trailing fully-empty rows/cols left behind by formatting.
    while (rows.length > 0 && (rows[rows.length - 1] ?? []).every((c) => c === '')) rows.pop();
    const width = rows.reduce((m, r) => Math.max(m, r.length), 0);
    if (width > 0 && rows.length * width > MAX_PARSE_CELLS) {
      const keep = Math.max(1, Math.floor(MAX_PARSE_CELLS / width));
      rows = rows.slice(0, keep);
      truncated.push({ sheet: sheetName, keptRows: keep });
    }
    const colWidths = (ws?.['!cols'] ?? []).map((c) => (c?.wpx ? Math.round(c.wpx) : c?.wch ? wchToPx(c.wch) : undefined));
    const sheet: SheetData = { id: nextId('sheet'), name: sheetName, rows, colWidths, pinnedCols: 0, frozenHeader: rows.length > 1, filters: {} };
    return colWidths.some((w) => w !== undefined) ? sheet : autofitAll(sheet);
  });

  const name = filename.replace(/\.[^.]+$/, '');
  const doc: SheetDoc = {
    id: nextId('doc'),
    name,
    kind: isText && sheets.length <= 1 ? 'csv' : 'xlsx',
    sheets: sheets.length > 0 ? sheets : [{ id: nextId('sheet'), name: 'Sheet1', rows: [], colWidths: [], pinnedCols: 0, frozenHeader: false, filters: {} }],
    activeSheet: 0,
    dirty: false,
    sourceLabel,
  };
  return { doc, truncated };
}

/** Build a document from an array-of-arrays (header row first): query results, API pages, etc. */
export function docFromAoa(name: string, aoa: (string | number | null | undefined)[][], sourceLabel: string): { doc: SheetDoc; keptRows: number } {
  let rows = aoa.map((r) => r.map((v) => (v == null ? '' : String(v))));
  const width = rows.reduce((m, r) => Math.max(m, r.length), 0);
  let keptRows = 0;
  if (width > 0 && rows.length * width > MAX_PARSE_CELLS) {
    keptRows = Math.max(1, Math.floor(MAX_PARSE_CELLS / width));
    rows = rows.slice(0, keptRows);
  }
  const sheet: SheetData = autofitAll({ id: nextId('sheet'), name: 'Sheet1', rows, colWidths: [], pinnedCols: 0, frozenHeader: rows.length > 1, filters: {} });
  const doc: SheetDoc = { id: nextId('doc'), name: (name || 'data').replace(/[\\/:*?"<>|]/g, '_').trim() || 'data', kind: 'xlsx', sheets: [sheet], activeSheet: 0, dirty: true, sourceLabel };
  return { doc, keptRows };
}

// ── build ───────────────────────────────────────────────────────────────────

/** Numbers become numbers, never dates / leading zeros (phone numbers, zip codes stay text). */
function coerce(v: string, isHeaderRow: boolean): string | number {
  if (isHeaderRow || v === '') return v;
  const t = v.trim();
  if (t === '' || (t[0] === '0' && t.length > 1 && t[1] !== '.')) return v;
  const n = Number(t);
  return Number.isFinite(n) && /^-?\d+(\.\d+)?$/.test(t) && t.length < 12 ? n : v;
}

function sheetToWorksheet(sheet: SheetData): XLSX.WorkSheet {
  const aoa = sheet.rows.map((row, r) => row.map((v) => coerce(v, r === 0 && sheet.frozenHeader)));
  const ws = XLSX.utils.aoa_to_sheet(aoa.length > 0 ? aoa : [[]]);
  if (sheet.colWidths.some((w) => w !== undefined)) {
    ws['!cols'] = sheet.colWidths.map((w) => (w !== undefined && w !== DEFAULT_COL_W ? { wch: pxToWch(w) } : {}));
  }
  return ws;
}

export function docToXlsxBlob(doc: SheetDoc): Blob {
  const wb = XLSX.utils.book_new();
  for (const sheet of doc.sheets) XLSX.utils.book_append_sheet(wb, sheetToWorksheet(sheet), sheet.name.slice(0, 31));
  const out = XLSX.write(wb, { type: 'array', bookType: 'xlsx' }) as ArrayBuffer;
  return new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

/** One sheet → properly-quoted CSV (RFC 4180, UTF-8 with BOM so Excel opens it correctly). */
export function sheetToCsvBlob(sheet: SheetData): Blob {
  const csv = XLSX.utils.sheet_to_csv(sheetToWorksheet(sheet));
  return new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' });
}

export function docFilename(doc: SheetDoc, kind: DocKind, sheet?: SheetData): string {
  const base = doc.name.replace(/[\\/:*?"<>|]/g, '_').trim() || 'sheet';
  if (kind === 'csv' && sheet && doc.sheets.length > 1) return `${base} - ${sheet.name}.csv`;
  return `${base}.${kind}`;
}

export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
